import { motion } from "motion/react"

interface PlayerTokenProps {
  playerId: string
  abbr: string
  teamSide: "home" | "away"
  status: "standing" | "prone" | "stunned"
  /** Resolved hue for this player's team. */
  teamHue: number
  /** When true, applies breathing glow to this token (acting player). */
  isActing?: boolean
  /** When true, applies gold breathing glow (narrative hover cross-link). */
  isHighlighted?: boolean
  /** When provided, clicking the token triggers player inspection. */
  onClick?: () => void
}

/**
 * Renders a Blood Bowl player token.
 *
 * Uses motion.dev's `layoutId` for smooth cross-cell transitions: when a player
 * moves from one GridCell to another, Motion animates the position change via
 * FLIP. Requires a LayoutGroup ancestor (provided by PitchGrid).
 *
 * Visual states:
 * - standing: circular token (default)
 * - prone: flattened rectangle, reduced opacity
 * - stunned: same as prone + ✕ indicator
 * - ball carrier: golden ball dot overlay
 * - isActing: gold glow around token (acting player)
 * - isHighlighted: gold glow around token (narrative hover)
 *
 * Team colors: derived from teamHue prop.
 */
export function PlayerToken({
  playerId,
  abbr,
  teamSide,
  status,
  teamHue,
  isActing = false,
  isHighlighted = false,
  onClick,
}: PlayerTokenProps) {
  const isProne = status === "prone"
  const isStunned = status === "stunned"
  const isFlat = isProne || isStunned

  const h = teamHue

  // Base token styles — derived from hue
  const baseStyle: React.CSSProperties = {
    background: `linear-gradient(180deg, oklch(0.40 0.14 ${h}), oklch(0.28 0.10 ${h}))`,
    borderColor: `oklch(0.50 0.15 ${h})`,
    color: `oklch(0.90 0.05 ${h})`,
  }

  // Shape/size changes for prone/stunned
  const shapeStyle: React.CSSProperties = isFlat
    ? {
        borderRadius: "30%",
        width: "85%",
        height: "50%",
        opacity: isStunned ? 0.5 : 0.7,
      }
    : {
        borderRadius: "50%",
        width: "80%",
        height: "80%",
      }

  // Gold glow on the inner token div — follows the token during layout animations
  const showGlow = isHighlighted || isActing
  const tokenBoxShadow = showGlow
    ? "0 0 8px oklch(0.70 0.16 85 / 0.6), 0 0 16px oklch(0.65 0.14 85 / 0.3), 0 2px 4px oklch(0 0 0 / 0.4)"
    : `inset 0 1px 0 oklch(0.55 0.10 ${h} / 0.4), 0 2px 4px oklch(0 0 0 / 0.5)`

  // Light flourish: triple text-shadow for ink-stamp depth on abbreviations
  const abbrTextShadow = `0 1px 0 oklch(0.20 0.08 ${h}), 0 1px 1px oklch(0 0 0 / 0.5), 0 0 3px oklch(0.50 0.15 ${h} / 0.3)`

  // Motion wrapper: only z-index lift when glowing (glow itself is on the inner div)
  const motionStyle: React.CSSProperties = {
    zIndex: showGlow ? 10 : undefined,
    cursor: onClick ? "pointer" : undefined,
  }

  return (
    <motion.div
      layoutId={playerId}
      className="relative flex h-full w-full items-center justify-center"
      transition={{
        type: "spring",
        stiffness: 300,
        damping: 30,
        duration: 0.3,
      }}
      onClick={onClick}
      style={motionStyle}
      data-testid="player-token"
      data-team={teamSide}
      data-status={status}
    >
      {/* Main token circle/rectangle */}
      <div
        className="relative flex items-center justify-center border-2 text-[9px] font-extrabold"
        style={{
          ...baseStyle,
          ...shapeStyle,
          boxShadow: tokenBoxShadow,
        }}
      >
        <span style={{ textShadow: abbrTextShadow }}>{abbr}</span>

        {/* Stunned indicator */}
        {isStunned && (
          <span
            className="absolute -top-0.5 -right-0.5 text-[8px] leading-none font-black"
            style={{ color: "oklch(0.60 0.22 28)" }}
            aria-label="stunned"
          >
            ✕
          </span>
        )}
      </div>
    </motion.div>
  )
}
